/**
 * "Add service bundle" wizard (tracker item 85): property, bundle, start date,
 * technician, a preview table of the visits with dates and prices, and the
 * Auto-renew checkbox pre-set from the bundle. Confirming creates the
 * enrollment and one job per visit in one transaction (server side).
 * Used on the customer page (property picker) and the property page (fixed).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createAPIClient, IBundlesApi, IUsersApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { formatJobDate, formatJobMoney } from '../../utils/jobPresentation';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';
import { localDateString, spanText } from './bundleUi';

type WizardApi = IBundlesApi & Pick<IUsersApi, 'listAssignableUsers'>;

interface WizardProps {
  /** Property detail page: the property is fixed */
  propertyId?: string;
  /** Customer page: the customer's properties to pick from */
  properties: Pick<types.Property, 'id' | 'address' | 'city' | 'state'>[];
  onClose: () => void;
  onAdded: (enrollment: types.CreatedEnrollment) => void;
}

type PreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; preview: types.BundlePreview }
  | { status: 'error'; message: string };

const dateOk = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));

export function AddBundleWizard({ propertyId: fixedPropertyId, properties, onClose, onAdded }: WizardProps) {
  const api = useMemo(() => createAPIClient() as WizardApi, []);
  const [bundles, setBundles] = useState<types.ServiceBundle[] | null>(null);
  const [users, setUsers] = useState<types.AssignableUser[]>([]);
  const [propertyId, setPropertyId] = useState(fixedPropertyId ?? (properties.length === 1 ? properties[0].id : ''));
  const [bundleId, setBundleId] = useState('');
  const [startDate, setStartDate] = useState(() => localDateString());
  const [assignedTo, setAssignedTo] = useState('');
  const [autoRenew, setAutoRenew] = useState(false);
  const [preview, setPreview] = useState<PreviewState>({ status: 'idle' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const dialog = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    Promise.allSettled([api.listServiceBundles({ isActive: true }), api.listAssignableUsers()]).then(([bundleResult, userResult]) => {
      if (!active) return;
      if (bundleResult.status === 'fulfilled') setBundles(bundleResult.value);
      else { setBundles([]); setLoadError(getErrorMessage(bundleResult.reason, 'Could not load the service bundles')); }
      if (userResult.status === 'fulfilled') setUsers(userResult.value);
    });
    return () => { active = false; };
  }, [api]);

  useEffect(() => {
    const origin = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.current?.focus();
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); origin?.focus(); };
  }, [onClose]);

  const bundle = bundles?.find((b) => b.id === bundleId) ?? null;

  // The Auto-renew checkbox starts from the bundle's default whenever another bundle is chosen
  useEffect(() => { if (bundle) setAutoRenew(bundle.autoRenewDefault); }, [bundle]);

  useEffect(() => {
    if (!bundleId || !propertyId || !dateOk(startDate)) { setPreview({ status: 'idle' }); return; }
    let active = true;
    setPreview({ status: 'loading' });
    api.previewServiceBundle(bundleId, { propertyId, startDate })
      .then((result) => { if (active) setPreview({ status: 'ok', preview: result }); })
      .catch((err) => { if (active) setPreview({ status: 'error', message: getErrorMessage(err, 'Could not preview the visits') }); });
    return () => { active = false; };
  }, [api, bundleId, propertyId, startDate]);

  const canConfirm = preview.status === 'ok' && !busy;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canConfirm || !bundleId || !propertyId) return;
    setBusy(true);
    setError(null);
    try {
      const created = await api.addBundleToProperty(propertyId, { bundleId, startDate, ...(assignedTo ? { assignedTo } : {}), autoRenew });
      onAdded(created);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not add the service bundle'));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" role="presentation">
      <div ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="add-bundle-title" className="max-h-full w-full overflow-y-auto rounded-t-lg bg-white p-5 shadow-xl focus:outline-none sm:max-w-3xl sm:rounded-lg">
        <h2 id="add-bundle-title" className="text-lg font-semibold text-gray-900">Add service bundle</h2>
        <p className="mt-1 text-sm text-gray-600">Creates every visit of the program as its own job, with its own item, date and price. The prices are fixed when you confirm.</p>
        {loadError && <Toast kind="error">{loadError}</Toast>}
        {error && <Toast kind="error">{error}</Toast>}
        <form onSubmit={submit} className="mt-4 space-y-4" noValidate>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {!fixedPropertyId && (
              <div>
                <label htmlFor="wizard-property" className={labelClass}>Property</label>
                <select id="wizard-property" className={inputClass} value={propertyId} disabled={busy} onChange={(e) => setPropertyId(e.target.value)}>
                  <option value="">Choose a property</option>
                  {properties.map((p) => <option key={p.id} value={p.id}>{p.address}, {p.city}</option>)}
                </select>
              </div>
            )}
            <div>
              <label htmlFor="wizard-bundle" className={labelClass}>Service bundle</label>
              <select id="wizard-bundle" className={inputClass} value={bundleId} disabled={busy || bundles === null} onChange={(e) => setBundleId(e.target.value)}>
                <option value="">{bundles === null ? 'Loading...' : bundles.length === 0 ? 'No active bundles' : 'Choose a bundle'}</option>
                {(bundles ?? []).map((b) => <option key={b.id} value={b.id}>{b.name} ({spanText(b.componentCount, b.visitSpanDays)})</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="wizard-start" className={labelClass}>Start date (first visit)</label>
              <input id="wizard-start" type="date" className={inputClass} value={startDate} disabled={busy} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <label htmlFor="wizard-technician" className={labelClass}>Technician (all visits)</label>
              <select id="wizard-technician" className={inputClass} value={assignedTo} disabled={busy} onChange={(e) => setAssignedTo(e.target.value)}>
                <option value="">Unassigned (route default)</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
              </select>
            </div>
          </div>

          <div aria-live="polite">
            {preview.status === 'idle' && <p className="text-sm text-gray-500">Choose a property, a bundle and a start date to see the visits.</p>}
            {preview.status === 'loading' && <p role="status" className="text-sm text-gray-500">Loading the visits...</p>}
            {preview.status === 'error' && <p role="alert" className="rounded bg-red-50 p-3 text-sm text-red-800">{preview.message}</p>}
            {preview.status === 'ok' && (
              <div>
                {preview.preview.skipped.length > 0 && (
                  <p role="note" className="mb-2 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    Not scheduled: {preview.preview.skipped.map((s) => `${s.itemName} (${s.reason === 'ITEM_DELETED' ? 'item deleted' : 'item inactive'})`).join(', ')}.
                  </p>
                )}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm" aria-label="Visits to create">
                    <thead className="bg-gray-50">
                      <tr>{['Visit', 'Item', 'Qty', 'Date', 'Unit price', 'Total'].map((h) => <th key={h} scope="col" className="px-3 py-2 text-left font-medium text-gray-600">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {preview.preview.visits.map((v) => (
                        <tr key={v.step}>
                          <td className="px-3 py-2">{v.step} of {preview.preview.stepCount}</td>
                          <td className="px-3 py-2 break-words">{v.itemName}</td>
                          <td className="px-3 py-2">{v.quantity}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{formatJobDate(v.plannedDate)}</td>
                          <td className="px-3 py-2">{formatJobMoney(v.unitPrice)}</td>
                          <td className="px-3 py-2">{formatJobMoney(v.lineTotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="font-semibold">
                        <td colSpan={5} className="px-3 py-2 text-right">Program total{Number(preview.preview.totalTax) > 0 ? ' (with tax)' : ''}</td>
                        <td className="px-3 py-2" data-testid="preview-total">{formatJobMoney(preview.preview.total)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-start gap-3">
            <input id="wizard-autorenew" type="checkbox" className="mt-1 h-5 w-5" checked={autoRenew} disabled={busy} onChange={(e) => setAutoRenew(e.target.checked)} />
            <div>
              <label htmlFor="wizard-autorenew" className="font-medium text-gray-900">Auto-renew</label>
              <p className="text-sm text-gray-600">When the last visit is done, prepare next season&apos;s renewal. It always waits for staff approval before anything is scheduled.</p>
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" className={secondaryButtonClass} onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className={primaryButtonClass} disabled={!canConfirm}>{busy ? 'Adding...' : 'Add bundle'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
